import React, { useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../Admin/Layout";
import { decryptData } from "../Utils/encryption";

// Lazy-loaded components for the Admin panel
const AdminDashboard = lazy(() => import("../Admin/AdminDashboard"));
const Category = lazy(() => import("../Admin/Category"));
const Game = lazy(() => import("../Admin/Game"));
const TermsConditions = lazy(() => import("../Admin/TermsConditions"));
const PrivacyPolicy = lazy(() => import("../Admin/PrivacyPolicy"));
const User = lazy(() => import("../Admin/User"));
const Contact = lazy(() => import("../Admin/Contact"));
const Blog = lazy(() => import("../Admin/Blog"));
const AdminProfile = lazy(() => import("../Admin/AdminProfile"));
const Faq = lazy(() => import("../Admin/Faq"));
const Transaction = lazy(() => import("../Admin/Transaction"));
const Subscriber = lazy(() => import("../Admin/Subscriber"));
const Discounts = lazy(() => import("../Admin/Discounts"));
const AdminBundles = lazy(() => import("../Admin/AdminBundles"));
const MaintenanceSetting = lazy(() => import("../Admin/MaintenanceSetting"));
const NotFound = lazy(() => import("../Admin/NotFound"));

const PAGE_TITLES = {
  "/admin": "Dashboard",
  "/admin/category": "Category",
  "/admin/games": "Games",
  "/admin/restore-games": "Games",
  "/admin/blog": "Blog",
  "/admin/user": "User",
  "/admin/profile": "Profile",
  "/admin/faq": "FAQ",
  "/admin/terms-conditions": "Terms & Conditions",
  "/admin/privacy-policy": "Privacy Policy",
  "/admin/contact": "Contact",
  "/admin/bundles": "Bundles",
  "/admin/order": "Orders",
  "/admin/subscriber": "Subscriber",
  "/admin/discounts": "Discounts",
  "/admin/maintenance": "System Settings"
};

const AdminRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem('role');
  const user = useSelector((state) => state.auth.user);
  const rolePermissions = useSelector((state) => state.permission.rolePermissions);
  const userEmail = user?.email ? decryptData(user.email) : "";
  const isSuperAdmin = userEmail === "admin@gmail.com" || role === "superadmin";

  // ✅ Hook 1
  useEffect(() => {
    document.title = `Admin • YOYO`;
    window.scrollTo(0, 0);
  }, []);

  // ✅ Hook 2
  useEffect(() => {
    if (role !== 'admin' && role !== 'superadmin') {
      navigate('/');
      return;
    }

    if (!isSuperAdmin && rolePermissions[role]) {
      const allowed = rolePermissions[role];
      const pageTitle = PAGE_TITLES[location.pathname];
      if (pageTitle && pageTitle !== "Profile" && !allowed.includes(pageTitle)) {
        navigate('/admin');
      }
    }
  }, [role, navigate, isSuperAdmin, rolePermissions, location.pathname]);

  return (
    <Layout>
      <Suspense>
        <Routes>
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/category" element={<Category />} />
          <Route path="/games" element={<Game />} />
          <Route path="/restore-games" element={<Game />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/user" element={<User />} />
          <Route path="/profile" element={<AdminProfile />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/bundles" element={<AdminBundles />} />
          <Route path="/order" element={<Transaction />} />
          <Route path="/subscriber" element={<Subscriber />} />
          <Route path="/discounts" element={<Discounts />} />
          <Route path="/maintenance" element={<MaintenanceSetting />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default AdminRoutes;
